import { Link, NavLink, Outlet } from 'react-router-dom';

/**
 * Top-nav layout for the public transparency site — deliberately NOT the
 * dark internal Shell.tsx sidebar used by every other app's (and this app's
 * own) staff console. This is a public government website: light background,
 * a horizontal nav anyone can use with no sign-in, and a small "MDA staff
 * sign-in" link tucked in the corner for the one part of this app that does
 * require it. See src/App.tsx — routes rendered inside this layout carry no
 * persona/auth gate whatsoever.
 */
const NAV: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/', label: 'Home', end: true },
  { to: '/datasets', label: 'Open Data' },
  { to: '/budget', label: 'Budget' },
  { to: '/foi', label: 'Freedom of Information' },
  { to: '/notices', label: 'Public Notices' },
  { to: '/parcels', label: 'My Parcel' },
  { to: '/integrity', label: 'Platform Integrity' },
];

export function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-white text-slate-900">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600 text-sm font-black text-white">
              SC
            </span>
            <span>
              <span className="block text-sm font-bold leading-tight text-slate-900">OpenGov Seychelles</span>
              <span className="block text-[11px] text-slate-500">Transparency &amp; Open Data Portal</span>
            </span>
          </Link>
          <Link
            to="/staff/login"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-brand-400 hover:bg-slate-50 hover:text-slate-900"
          >
            MDA staff sign-in
          </Link>
        </div>
        <nav className="border-t border-slate-100 bg-slate-50">
          <div className="mx-auto flex max-w-6xl flex-wrap gap-1 px-4 py-1.5 sm:px-6">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    isActive ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-white hover:text-slate-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main id="main-content" className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 py-6">
        <div className="mx-auto max-w-6xl px-6 text-xs leading-relaxed text-slate-500">
          <p>
            Republic of Seychelles — One Gov Seychelles. Published under the Access to Information Act 2018 and the
            National Open Data Policy.
          </p>
          <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <a href="/api/v1/opengov/notices/feed.rss" className="underline decoration-slate-300 hover:text-slate-700">
              Public Notices RSS feed
            </a>
            <Link to="/integrity" className="underline decoration-slate-300 hover:text-slate-700">
              Platform integrity &amp; audit anchors
            </Link>
            <Link to="/staff/login" className="underline decoration-slate-300 hover:text-slate-700">
              MDA staff console
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
